import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { createUser, fetchLevels, fetchEmployeeTypes, fetchUserRoles, fetchDepartments, fetchUsers, setDottedReportees } from '../services/api';
import Autocomplete from '@mui/material/Autocomplete';

const AddUser = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    contact: '',
    password: '',
    confirm_password: '',
    level_id: '',
    employee_type_id: '',
    user_role_id: '',
    work_location: '',
    department_id: '',
    direct_reportee_id: '',
    dotted_reportee_ids: [],
  });

  const [levels, setLevels] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadFormData();
  }, []);

  const loadFormData = async () => {
    setLoading(true);
    try {
      const [levelsData, employeeTypesData, userRolesData, departmentsData, usersData] = await Promise.all([
        fetchLevels(),
        fetchEmployeeTypes(),
        fetchUserRoles(),
        fetchDepartments(),
        fetchUsers(),
      ]);
      setLevels(levelsData);
      setEmployeeTypes(employeeTypesData);
      setUserRoles(userRolesData);
      setDepartments(departmentsData);
      setUsers(usersData);
    } catch (error) {
      setError('Failed to load form data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleDottedChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      dotted_reportee_ids: value.map(u => u.id)
    }));
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.contact.trim()) {
      setError('Contact number is required');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.level_id) {
      setError('Level is required');
      return false;
    }
    if (!formData.employee_type_id) {
      setError('Employee type is required');
      return false;
    }
    if (!formData.user_role_id) {
      setError('User role is required');
      return false;
    }
    if (!formData.work_location) {
      setError('Work location is required');
      return false;
    }
    if (!formData.department_id) {
      setError('Department is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const userData = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim().toLowerCase(),
        contact: formData.contact.trim(),
        password: formData.password,
        level_id: parseInt(formData.level_id),
        employee_type_id: parseInt(formData.employee_type_id),
        user_role_id: parseInt(formData.user_role_id),
        work_location: formData.work_location,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        direct_reportee_id: formData.direct_reportee_id ? parseInt(formData.direct_reportee_id) : null,
      };

      const created = await createUser(userData);
      if (formData.dotted_reportee_ids.length > 0) {
        await setDottedReportees(created.id, formData.dotted_reportee_ids);
      }
      setSuccess('User created successfully!');
      setFormData({
        full_name: '',
        email: '',
        contact: '',
        password: '',
        confirm_password: '',
        level_id: '',
        employee_type_id: '',
        user_role_id: '',
        work_location: '',
        department_id: '',
        direct_reportee_id: '',
        dotted_reportee_ids: [],
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    setFormData({
      full_name: '',
      email: '',
      contact: '',
      password: '',
      confirm_password: '',
      level_id: '',
      employee_type_id: '',
      user_role_id: '',
      work_location: '',
      department_id: '',
      direct_reportee_id: '',
      dotted_reportee_ids: [],
    });
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonAddIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Add New User
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Level</InputLabel>
                <Select
                  name="level_id"
                  value={formData.level_id}
                  onChange={handleInputChange}
                  label="Level"
                >
                  {levels.map((level) => (
                    <MenuItem key={level.id} value={level.id}>
                      {level.level_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Employee Type</InputLabel>
                <Select
                  name="employee_type_id"
                  value={formData.employee_type_id}
                  onChange={handleInputChange}
                  label="Employee Type"
                >
                  {employeeTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>User Role</InputLabel>
                <Select
                  name="user_role_id"
                  value={formData.user_role_id}
                  onChange={handleInputChange}
                  label="User Role"
                >
                  {userRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Work Location</InputLabel>
                <Select
                  name="work_location"
                  value={formData.work_location}
                  onChange={handleInputChange}
                  label="Work Location"
                >
                  <MenuItem value="Onsite">Onsite</MenuItem>
                  <MenuItem value="Offshore">Offshore</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleInputChange}
                  label="Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Direct Reportee</InputLabel>
                <Select
                  name="direct_reportee_id"
                  value={formData.direct_reportee_id}
                  onChange={handleInputChange}
                  label="Direct Reportee"
                >
                  <MenuItem value="">None</MenuItem>
                  {users.filter(u => u.id !== undefined && u.email !== formData.email).map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={users.filter(u => u.id !== undefined && u.email !== formData.email && u.id !== parseInt(formData.direct_reportee_id))}
                getOptionLabel={(option) => `${option.full_name} (${option.email})`}
                value={users.filter(u => formData.dotted_reportee_ids.includes(u.id))}
                onChange={handleDottedChange}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" label="Dotted-line Reportees" placeholder="Select users" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              disabled={submitting}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : 'Create User'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default AddUser; 